/**
 * Quotas centralisés DooDates - Nouveau modèle simplifié 3 niveaux
 *
 * ⚠️ SOURCE DE VÉRITÉ UNIQUE - Ne pas dupliquer ces valeurs ailleurs
 *
 * Système de quotas simplifié :
 * - Anonyme : 3 conversations IA à vie, 5 messages max par conversation
 * - Gratuit : 5 conversations IA à vie, 5 messages max par conversation, plafond 10 sondages actifs
 * - Solo : IA illimitée, 10 messages max par conversation, exports complets, intégration calendrier
 *
 * Principes :
 * - La création manuelle de sondages est TOUJOURS gratuite et illimitée (anonyme inclus)
 * - L'IA est le levier de conversion (goûter la magie → payer pour la garder)
 * - 3 IA anonyme = aimant d'acquisition ; 5 IA Free = passage vers Solo ; illimité Solo
 * - Fair Use invisible pour protéger les coûts IA (jamais affiché à l'utilisateur)
 *
 * Dernière mise à jour : 11 Mai 2026 - IA ouverte aux anonymes, resserrement Free
 */

// ============================================================================
// QUOTAS CONVERSATIONS IA
// ============================================================================

/**
 * Nombre maximum de conversations IA par utilisateur
 *
 * Une conversation = une session de création de sondage avec l'IA
 * Les modifications d'un sondage existant ne comptent pas comme nouvelle conversation
 */
export const CONVERSATION_QUOTAS = {
  /** Utilisateurs anonymes : 3 conversations IA à vie (non renouvelable) */
  ANONYMOUS: 3,

  /** Utilisateurs gratuits : 5 conversations IA à vie (non renouvelable) */
  FREE: 5,

  /** Utilisateurs Solo : IA illimitée */
  SOLO: 999999, // Illimité (avec Fair Use invisible de 100/mois)
} as const;

// ============================================================================
// QUOTAS MESSAGES IA
// ============================================================================

/**
 * Nombre maximum de messages IA par conversation
 *
 * Limite de profondeur pour éviter les conversations infinies
 * Différencie les plans Gratuit (5) vs Solo (10)
 */
export const AI_MESSAGE_QUOTAS = {
  /** Utilisateurs anonymes : 5 messages maximum par conversation (aligné Free) */
  ANONYMOUS: 5,

  /** Utilisateurs gratuits : 5 messages maximum par conversation */
  FREE: 5,

  /** Utilisateurs Solo : 10 messages maximum par conversation */
  SOLO: 10,
} as const;

// ============================================================================
// QUOTAS STOCKAGE
// ============================================================================

/**
 * Limites de stockage localStorage (approximatif)
 */
export const STORAGE_QUOTAS = {
  /** Utilisateurs anonymes : 50 MB */
  ANONYMOUS: 50,

  /** Utilisateurs gratuits : 500 MB */
  FREE: 500,

  /** Utilisateurs Solo : 2000 MB (2 GB) */
  SOLO: 2000,
} as const;

// ============================================================================
// PLANS & PRICING - Modèle Simplifié 3 Niveaux
// ============================================================================

export type PlanId = "ANONYMOUS" | "FREE" | "SOLO";

export const PLANS = {
  ANONYMOUS: {
    id: "ANONYMOUS",
    name: "Anonyme",
    price: 0,
    period: "lifetime",
    features: {
      aiConversations: 3, // 3 conversations IA à vie (aimant acquisition)
      maxMessageDepth: 5,
      polls: "unlimited", // Création manuelle illimitée
      participants: "unlimited",
      dashboard: false,
      notifications: false,
      modifications: false,
      exportFormats: [],
      calendarIntegration: false,
      earlyAccess: false,
    },
  },
  FREE: {
    id: "FREE",
    name: "Gratuit",
    price: 0,
    period: "lifetime",
    features: {
      aiConversations: 5, // 5 conversations IA à vie (resserré, pousse vers Solo)
      maxMessageDepth: 5,
      polls: 10, // Plafond 10 sondages actifs
      participants: "unlimited",
      dashboard: true,
      notifications: true,
      modifications: true,
      exportFormats: ["CSV", "JSON", "Markdown"],
      calendarIntegration: false,
      earlyAccess: false,
    },
  },
  SOLO: {
    id: "SOLO",
    name: "Solo",
    price: 30, // 30€/an (2 mois offerts)
    period: "annual",
    features: {
      aiConversations: "unlimited", // IA illimitée
      maxMessageDepth: 10,
      polls: "unlimited",
      participants: "unlimited",
      dashboard: true,
      notifications: true,
      modifications: true,
      exportFormats: ["CSV", "Excel", "PDF", "JSON", "Markdown"],
      calendarIntegration: true, // Google Calendar
      earlyAccess: true, // Fonctions IA en avant-première
    },
  },
} as const;

// ============================================================================
// RÉTENTION DES DONNÉES
// ============================================================================

/**
 * Durée de conservation des données avant auto-suppression.
 *
 * Depuis le 2026-07-03 (purge v2), le décompte part de la DERNIÈRE ACTIVITÉ
 * du sondage (création, modification ou dernière réponse), plus de la date
 * de création. Voir supabase/migrations/20260703091000_purge_policy_v2.sql.
 */
export const RETENTION_DAYS = {
  /** Utilisateurs anonymes : 90 jours après dernière activité */
  ANONYMOUS: 90,

  /** Utilisateurs gratuits : 90 jours après dernière activité */
  FREE: 90,

  /** Utilisateurs payants : illimité (sauf poll_retention opt-in 12/24 mois) */
  SOLO: 999999,
} as const;

// ============================================================================
// PLAFOND SONDAGES ACTIFS
// ============================================================================

/**
 * Nombre maximum de sondages actifs par plan
 *
 * Un sondage est "actif" tant qu'il n'est pas archivé/supprimé.
 * Free : 10 max — au-delà, archiver ou passer Solo.
 * Anonyme et Solo : illimités.
 */
export const MAX_ACTIVE_POLLS = {
  /** Utilisateurs anonymes : illimité (création manuelle libre) */
  ANONYMOUS: "unlimited",

  /** Utilisateurs gratuits : 10 sondages actifs maximum */
  FREE: 10,

  /** Utilisateurs Solo : illimité */
  SOLO: "unlimited",
} as const;

/**
 * Nombre maximum de modifications AI (editPollFromText) appliquées sur une
 * MÊME proposition de sondage avant que l'utilisateur soit invité à créer
 * le sondage. Sert à plafonner le coût Gemini : chaque modif consomme un
 * appel LLM. Reset à 0 à chaque nouvelle génération (generatePollFromText).
 *
 * Cf. spec : Docs/SPECS/2026-05-12-spec-modifications-proposition-sondage.md
 */
export const MAX_MODIFICATIONS_PER_POLL = 3;

// ============================================================================
// FORM POLLS — FAIR USE IA (invisible)
// ============================================================================

/**
 * Quotas IA spécifiques au produit Form Polls (DooQuest).
 *
 * Doctrine : cf. Docs/QUOTAS&PRICING/2026-06-07-DECISION-PRICING-FORM-POLLS.md
 * Le produit Form garde son pricing propre (Gratuit / DooQuest Pro 70 €/an) et
 * n'est PAS soumis au modèle ANONYMOUS/FREE/SOLO des Date Polls.
 *
 * Principe « Découpler le Stockage de l'Intelligence » : la collecte de
 * réponses est illimitée (stockage ≈ gratuit), seule l'IA est plafonnée.
 *
 * - FREE_LIFETIME : dégustation IA offerte pour découvrir la valeur, puis paywall.
 * - PRO_FAIR_USE_MONTHLY : plafond technique invisible (jamais affiché à
 *   l'utilisateur, présenté comme « IA illimitée* »), protège la marge contre
 *   les abus automatisés. ~7,20 €/an de coût Gemini au pire cas.
 */
export const FORM_AI_QUOTAS = {
  /** Gratuit : 5 actions IA à vie (génération + analyse), non renouvelables */
  FREE_LIFETIME: 5,

  /** Pro : Fair Use invisible de 600 requêtes IA / mois (affiché « illimité ») */
  PRO_FAIR_USE_MONTHLY: 600,
} as const;

// ============================================================================
// HELPERS - Nouveau modèle 3 niveaux
// ============================================================================

/**
 * Récupère les limites de conversations selon le plan
 */
export function getConversationLimit(planId: PlanId): number {
  return CONVERSATION_QUOTAS[planId];
}

/**
 * Récupère les limites de messages IA selon le plan
 */
export function getAiMessageLimit(planId: PlanId): number {
  return AI_MESSAGE_QUOTAS[planId];
}

/**
 * Récupère les limites de stockage selon le plan
 */
export function getStorageLimit(planId: PlanId): number {
  return STORAGE_QUOTAS[planId];
}

/**
 * Récupère les jours de rétention selon le plan
 */
export function getRetentionDays(planId: PlanId): number {
  return RETENTION_DAYS[planId];
}

/**
 * Récupère le plafond de sondages actifs selon le plan
 * Renvoie `null` si illimité.
 */
export function getMaxActivePolls(planId: PlanId): number | null {
  const limit = MAX_ACTIVE_POLLS[planId];
  return limit === "unlimited" ? null : limit;
}

/**
 * Vérifie si le plan a accès à l'IA
 */
export function hasAiAccess(planId: PlanId): boolean {
  const aiConversations = PLANS[planId].features.aiConversations;
  return (
    (typeof aiConversations === "number" && aiConversations > 0) ||
    aiConversations === "unlimited"
  );
}

/**
 * Vérifie si le plan a accès aux exports complets
 */
export function hasFullExports(planId: PlanId): boolean {
  const formats = PLANS[planId].features.exportFormats as readonly string[];
  return formats.includes("Excel") || formats.includes("PDF");
}

/**
 * Vérifie si le plan a accès à l'intégration calendrier
 */
export function hasCalendarIntegration(planId: PlanId): boolean {
  return PLANS[planId].features.calendarIntegration;
}

/**
 * Vérifie si le plan a accès à l'early access
 */
export function hasEarlyAccess(planId: PlanId): boolean {
  return PLANS[planId].features.earlyAccess;
}

/**
 * Récupère toutes les fonctionnalités d'un plan
 */
export function getPlanFeatures(planId: PlanId) {
  return PLANS[planId].features;
}

/**
 * Détermine le plan d'un utilisateur selon son état d'authentification et son abonnement
 */
export function determineUserPlan(
  isAuthenticated: boolean,
  hasSoloSubscription: boolean,
): PlanId {
  if (!isAuthenticated) return "ANONYMOUS";
  if (hasSoloSubscription) return "SOLO";
  return "FREE";
}

// ============================================================================
// PLANS PAYANTS — abstraction centrale (multi-produits)
// ============================================================================

/**
 * Valeurs de `profiles.plan_type` considérées comme un abonnement PAYANT.
 *
 * - `solo`         : abonnement suite / Date Polls (30 €/an).
 * - `dooquest_pro` : abonnement Form Polls (DooQuest Pro, 70 €/an).
 *   (`dooform_pro` = ancienne valeur live, conservée le temps de la migration
 *   des lignes existantes ; à retirer une fois la base migrée.)
 *
 * ⚠️ SOURCE DE VÉRITÉ : tout garde-fou (front ou SQL) qui distingue "payant"
 * vs "gratuit" doit s'appuyer sur cette liste (front) ou sur la fonction SQL
 * `public.is_paid_plan(text)` (DB), jamais sur un littéral `'solo'` isolé.
 * Cela garantit qu'un nouveau plan payant est reconnu partout (UI paywall,
 * rétention/purge, plafond de sondages, Fair Use IA).
 */
export const PAID_PLAN_TYPES = ["solo", "dooquest_pro", "dooform_pro"] as const;

/** Vrai si `plan_type` correspond à un abonnement payant (insensible à la casse). */
export function isPaidPlanType(planType?: string | null): boolean {
  if (!planType) return false;
  return (PAID_PLAN_TYPES as readonly string[]).includes(
    planType.toLowerCase().trim(),
  );
}
